import Entities from 'constants/entities';
import { MAX_DIMENSION, MIN_DIMENSION } from 'constants/defaults';
import { FACTION_ASSETS } from 'constants/faction';
import { coordinateKey, createId, isValidColor } from 'utils/common';

export const SECTOR_IMPORT_VERSION = 1;
const ROUTE_WIDTHS = ['thin', 'normal', 'wide'];
const ROUTE_TYPES = ['solid', 'dotted', 'short', 'long'];

export class SectorImportError extends Error {
  constructor(errors) {
    super('The sector JSON is invalid');
    this.name = 'SectorImportError';
    this.errors = errors;
  }
}

const isObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const addError = (errors, path, message) => errors.push({ path, message });

const normalizeTags = (tags, path, errors) => {
  if (tags === undefined) {
    return { keys: undefined, visibility: {} };
  }
  if (!Array.isArray(tags)) {
    addError(errors, path, 'must be an array');
    return { keys: [], visibility: {} };
  }

  const visibility = {};
  const keys = tags.reduce((result, tag, index) => {
    const tagPath = `${path}[${index}]`;
    if (typeof tag === 'string' && tag) {
      return [...result, tag];
    }
    if (isObject(tag) && typeof tag.key === 'string' && tag.key) {
      if (tag.hidden === true) {
        visibility[`tag.${tag.key}`] = false;
      }
      return [...result, tag.key];
    }
    addError(errors, tagPath, 'must be a tag key or an object with a key');
    return result;
  }, []);

  return { keys, visibility };
};

const normalizeVisibility = (node, tagVisibility, path, errors) => {
  const visibility = { ...tagVisibility };
  if (node.visibility !== undefined) {
    if (!isObject(node.visibility)) {
      addError(errors, `${path}.visibility`, 'must be an object');
    } else {
      Object.keys(node.visibility).forEach(key => {
        if (typeof node.visibility[key] !== 'boolean') {
          addError(errors, `${path}.visibility.${key}`, 'must be a boolean');
        } else {
          visibility[key] = node.visibility[key];
        }
      });
    }
  }
  if (node.descriptionHidden === true) {
    visibility['attr.description'] = false;
  }
  if (isObject(node.attributeVisibility)) {
    Object.keys(node.attributeVisibility).forEach(key => {
      const isVisible = node.attributeVisibility[key];
      if (typeof isVisible !== 'boolean') {
        addError(
          errors,
          `${path}.attributeVisibility.${key}`,
          'must be a boolean',
        );
      } else {
        visibility[`attr.${key}`] = isVisible;
      }
    });
  }
  return visibility;
};

const normalizeNotes = node => {
  if (node.notes === undefined) {
    return [];
  }
  return Array.isArray(node.notes) ? node.notes : [node.notes];
};

export const parseSectorImport = definition => {
  if (typeof definition !== 'string') {
    return definition;
  }
  try {
    return JSON.parse(definition);
  } catch (error) {
    throw new SectorImportError([
      { path: '$', message: `contains invalid JSON: ${error.message}` },
    ]);
  }
};

export const importSectorDefinition = (
  definition,
  { createEntityId = createId } = {},
) => {
  const input = parseSectorImport(definition);
  const errors = [];
  if (!isObject(input)) {
    throw new SectorImportError([{ path: '$', message: 'must be an object' }]);
  }
  if (input.version !== SECTOR_IMPORT_VERSION) {
    addError(errors, '$.version', `must equal ${SECTOR_IMPORT_VERSION}`);
  }
  if (!isObject(input.sector)) {
    addError(errors, '$.sector', 'must be an object');
    throw new SectorImportError(errors);
  }

  const sector = input.sector;
  if (typeof sector.name !== 'string' || !sector.name.trim()) {
    addError(errors, '$.sector.name', 'must be a non-empty string');
  }
  ['rows', 'columns'].forEach(key => {
    const value = sector[key];
    if (
      !Number.isInteger(value) ||
      value < MIN_DIMENSION ||
      value > MAX_DIMENSION
    ) {
      addError(
        errors,
        `$.sector.${key}`,
        `must be an integer from ${MIN_DIMENSION} to ${MAX_DIMENSION}`,
      );
    }
  });

  const sectorId = createEntityId();
  const entities = { [Entities.sector.key]: {} };
  const occupiedCoordinates = {};
  const references = {};

  const addEntity = (entityType, entityId, entity) => {
    entities[entityType] = {
      ...(entities[entityType] || {}),
      [entityId]: entity,
    };
  };

  const visitNode = (node, entityType, entityId, parent, parentType, path) => {
    if (!isObject(node)) {
      addError(errors, path, 'must be an object');
      return;
    }
    if (typeof node.name !== 'string' || !node.name.trim()) {
      addError(errors, `${path}.name`, 'must be a non-empty string');
    }
    if (node.id !== undefined) {
      if (typeof node.id !== 'string' || !node.id) {
        addError(errors, `${path}.id`, 'must be a non-empty string');
      } else if (references[node.id]) {
        addError(errors, `${path}.id`, `duplicates the reference ${node.id}`);
      } else {
        references[node.id] = { entityId, entityType };
      }
    }

    const { keys: tags, visibility: tagVisibility } = normalizeTags(
      node.tags,
      `${path}.tags`,
      errors,
    );
    const attributes = isObject(node.attributes) ? { ...node.attributes } : {};
    if (node.attributes !== undefined && !isObject(node.attributes)) {
      addError(errors, `${path}.attributes`, 'must be an object');
    }
    if (node.description !== undefined) {
      if (typeof node.description !== 'string') {
        addError(errors, `${path}.description`, 'must be a string');
      } else {
        attributes.description = node.description;
      }
    }
    if (tags !== undefined) {
      attributes.tags = tags;
    }
    const attributeDefinitions = Entities[entityType].attributes || [];
    const allowedAttributes = ['description', 'tags'].concat(
      attributeDefinitions.map(({ key }) => key),
    );
    Object.keys(attributes).forEach(key => {
      if (allowedAttributes.indexOf(key) < 0) {
        addError(
          errors,
          `${path}.attributes.${key}`,
          `is not supported by ${entityType}`,
        );
        return;
      }
      const definition = attributeDefinitions.find(item => item.key === key);
      const options = Object.keys((definition || {}).attributes || {});
      if (definition && options.length && options.indexOf(attributes[key]) < 0) {
        addError(
          errors,
          `${path}.attributes.${key}`,
          `must be one of: ${options.join(', ')}`,
        );
      }
    });

    const entity = {
      name: typeof node.name === 'string' ? node.name.trim() : '',
      attributes,
      visibility: normalizeVisibility(node, tagVisibility, path, errors),
    };
    if (entityType === Entities.sector.key) {
      entity.rows = node.rows;
      entity.columns = node.columns;
    } else {
      entity.parent = parent;
      entity.parentEntity = parentType;
      entity.sector = sectorId;
      entity.isHidden = node.hidden === true || node.isHidden === true;
    }
    if (Entities[entityType].topLevel) {
      ['x', 'y'].forEach(key => {
        if (!Number.isInteger(node[key])) {
          addError(errors, `${path}.${key}`, 'must be an integer');
        } else {
          entity[key] = node[key];
        }
      });
      if (
        Number.isInteger(node.x) &&
        Number.isInteger(node.y) &&
        Number.isInteger(sector.columns) &&
        Number.isInteger(sector.rows)
      ) {
        if (
          node.x < 1 ||
          node.x > sector.columns ||
          node.y < 1 ||
          node.y > sector.rows
        ) {
          addError(errors, path, 'has coordinates outside the sector');
        }
        const coordinate = `${node.x},${node.y}`;
        if (occupiedCoordinates[coordinate]) {
          addError(
            errors,
            path,
            `shares coordinates with ${occupiedCoordinates[coordinate]}`,
          );
        } else {
          occupiedCoordinates[coordinate] = path;
        }
      }
    }

    addEntity(entityType, entityId, entity);

    const notes = normalizeNotes(node);
    notes.forEach((note, index) => {
      const notePath = `${path}.notes[${index}]`;
      const noteObject = typeof note === 'string' ? { text: note } : note;
      if (!isObject(noteObject) || typeof noteObject.text !== 'string') {
        addError(errors, notePath, 'must be text or an object with text');
        return;
      }
      const noteId = createEntityId();
      addEntity(Entities.note.key, noteId, {
        name: noteObject.name || 'Note',
        parent: entityId,
        parentEntity: entityType,
        sector: sectorId,
        isHidden: noteObject.hidden !== false,
        attributes: { description: noteObject.text },
        visibility: {},
      });
    });

    const children = node.children === undefined ? [] : node.children;
    if (!Array.isArray(children)) {
      addError(errors, `${path}.children`, 'must be an array');
      return;
    }
    children.forEach((child, index) => {
      const childPath = `${path}.children[${index}]`;
      if (!isObject(child) || typeof child.type !== 'string') {
        addError(errors, childPath, 'must be an object with a type');
        return;
      }
      if (!Entities[child.type] || Entities[child.type].extraneous) {
        addError(
          errors,
          `${childPath}.type`,
          'is not an importable entity type',
        );
        return;
      }
      if (Entities[entityType].children.indexOf(child.type) < 0) {
        addError(
          errors,
          `${childPath}.type`,
          `${child.type} cannot be a child of ${entityType}`,
        );
        return;
      }
      visitNode(
        child,
        child.type,
        createEntityId(),
        entityId,
        entityType,
        childPath,
      );
    });
  };

  visitNode(
    sector,
    Entities.sector.key,
    sectorId,
    undefined,
    undefined,
    '$.sector',
  );

  const resolveReference = (reference, path) => {
    if (typeof reference !== 'string' || !reference) {
      addError(errors, path, 'must be a non-empty entity reference');
      return {};
    }
    if (!references[reference]) {
      addError(errors, path, `references unknown entity ${reference}`);
      return {};
    }
    return references[reference];
  };

  const factions = {};
  const factionList = input.factions === undefined ? [] : input.factions;
  if (!Array.isArray(factionList)) {
    addError(errors, '$.factions', 'must be an array');
  } else {
    factionList.forEach((faction, index) => {
      const path = `$.factions[${index}]`;
      if (!isObject(faction)) {
        addError(errors, path, 'must be an object');
        return;
      }
      if (typeof faction.name !== 'string' || !faction.name.trim()) {
        addError(errors, `${path}.name`, 'must be a non-empty string');
      }
      const factionId = faction.id || createEntityId();
      if (factions[factionId]) {
        addError(errors, `${path}.id`, `duplicates faction ${factionId}`);
        return;
      }
      const { id, assets: assetList = [], homeworld, ...factionData } = faction;
      const normalizedFaction = { ...factionData, assets: {} };
      if (homeworld !== undefined) {
        const resolved = resolveReference(homeworld, `${path}.homeworld`);
        normalizedFaction.homeworld = resolved.entityId;
        normalizedFaction.homeworldEntity = resolved.entityType;
      }
      if (!Array.isArray(assetList)) {
        addError(errors, `${path}.assets`, 'must be an array');
      } else {
        assetList.forEach((asset, assetIndex) => {
          const assetPath = `${path}.assets[${assetIndex}]`;
          if (!isObject(asset) || typeof asset.type !== 'string') {
            addError(errors, assetPath, 'must be an object with a type');
            return;
          }
          if (!FACTION_ASSETS[asset.type]) {
            addError(
              errors,
              `${assetPath}.type`,
              `is not a recognized faction asset: ${asset.type}`,
            );
          }
          const { id: assetId = createEntityId(), location, ...assetData } = asset;
          if (normalizedFaction.assets[assetId]) {
            addError(errors, `${assetPath}.id`, `duplicates asset ${assetId}`);
            return;
          }
          const normalizedAsset = { ...assetData };
          if (location !== undefined) {
            const resolved = resolveReference(location, `${assetPath}.location`);
            normalizedAsset.location = resolved.entityId;
            normalizedAsset.locationEntity = resolved.entityType;
          }
          normalizedFaction.assets[assetId] = normalizedAsset;
        });
      }
      factions[factionId] = normalizedFaction;
    });
  }

  const layers = {};
  const layerList = input.layers === undefined ? [] : input.layers;
  if (!Array.isArray(layerList)) {
    addError(errors, '$.layers', 'must be an array');
  } else {
    layerList.forEach((layer, index) => {
      const path = `$.layers[${index}]`;
      if (!isObject(layer) || typeof layer.name !== 'string' || !layer.name) {
        addError(errors, path, 'must be an object with a name');
        return;
      }
      const layerId = layer.id || createEntityId();
      if (layers[layerId]) {
        addError(errors, `${path}.id`, `duplicates layer ${layerId}`);
        return;
      }
      const regionIds = {};
      const regions = {};
      const regionList = layer.regions === undefined ? [] : layer.regions;
      if (!Array.isArray(regionList)) {
        addError(errors, `${path}.regions`, 'must be an array');
      } else {
        regionList.forEach((region, regionIndex) => {
          const regionPath = `${path}.regions[${regionIndex}]`;
          if (!isObject(region) || typeof region.name !== 'string') {
            addError(errors, regionPath, 'must be an object with a name');
            return;
          }
          if (region.color !== undefined && !isValidColor(region.color)) {
            addError(errors, `${regionPath}.color`, 'must be a hex color');
          }
          const sourceId = region.id || `region-${regionIndex + 1}`;
          const regionId = region.id || createEntityId();
          if (regionIds[sourceId]) {
            addError(errors, `${regionPath}.id`, `duplicates region ${sourceId}`);
            return;
          }
          regionIds[sourceId] = regionId;
          const { id, hidden, ...regionData } = region;
          regions[regionId] = {
            ...regionData,
            isHidden: hidden === true || region.isHidden === true,
          };
        });
      }
      const hexes = {};
      const hexList = layer.hexes === undefined ? [] : layer.hexes;
      if (!Array.isArray(hexList)) {
        addError(errors, `${path}.hexes`, 'must be an array');
      } else {
        hexList.forEach((hex, hexIndex) => {
          const hexPath = `${path}.hexes[${hexIndex}]`;
          if (!isObject(hex) || !Number.isInteger(hex.x) || !Number.isInteger(hex.y)) {
            addError(errors, hexPath, 'must contain integer x and y coordinates');
            return;
          }
          if (hex.x < 1 || hex.x > sector.columns || hex.y < 1 || hex.y > sector.rows) {
            addError(errors, hexPath, 'has coordinates outside the sector');
          }
          const hexRegions = Array.isArray(hex.regions) ? hex.regions : [];
          if (!Array.isArray(hex.regions)) {
            addError(errors, `${hexPath}.regions`, 'must be an array');
          }
          const mappedRegions = hexRegions.reduce((result, regionId) => {
            if (!regionIds[regionId]) {
              addError(errors, `${hexPath}.regions`, `references unknown region ${regionId}`);
              return result;
            }
            return [...result, regionIds[regionId]];
          }, []);
          hexes[coordinateKey(hex.x, hex.y)] = { regions: mappedRegions };
        });
      }
      const layerData = { ...layer };
      delete layerData.id;
      delete layerData.hidden;
      delete layerData.regions;
      delete layerData.hexes;
      layers[layerId] = {
        ...layerData,
        isHidden: layer.hidden === true || layer.isHidden === true,
        regions,
        hexes,
      };
    });
  }

  const routes = {};
  const routeList = input.routes === undefined ? [] : input.routes;
  if (!Array.isArray(routeList)) {
    addError(errors, '$.routes', 'must be an array');
  } else {
    routeList.forEach((route, index) => {
      const path = `$.routes[${index}]`;
      if (!isObject(route) || !Array.isArray(route.points) || route.points.length < 2) {
        addError(errors, path, 'must have at least two points');
        return;
      }
      if (route.color !== undefined && !isValidColor(route.color)) {
        addError(errors, `${path}.color`, 'must be a hex color');
      }
      if (route.width !== undefined && ROUTE_WIDTHS.indexOf(route.width) < 0) {
        addError(errors, `${path}.width`, 'must be thin, normal, or wide');
      }
      if (route.type !== undefined && ROUTE_TYPES.indexOf(route.type) < 0) {
        addError(errors, `${path}.type`, 'must be solid, dotted, short, or long');
      }
      const routeId = route.id || createEntityId();
      if (routes[routeId]) {
        addError(errors, `${path}.id`, `duplicates route ${routeId}`);
        return;
      }
      const points = route.points.map((point, pointIndex) => {
        const pointPath = `${path}.points[${pointIndex}]`;
        if (!isObject(point) || !Number.isInteger(point.x) || !Number.isInteger(point.y)) {
          addError(errors, pointPath, 'must contain integer x and y coordinates');
          return undefined;
        }
        if (point.x < 1 || point.x > sector.columns || point.y < 1 || point.y > sector.rows) {
          addError(errors, pointPath, 'has coordinates outside the sector');
        }
        return coordinateKey(point.x, point.y);
      });
      const routeData = { ...route };
      delete routeData.id;
      delete routeData.hidden;
      delete routeData.points;
      routes[routeId] = {
        color: '#dbdbdb',
        width: 'normal',
        type: 'solid',
        ...routeData,
        route: points.filter(point => point),
        isHidden: route.hidden === true || route.isHidden === true,
      };
    });
  }

  if (Object.keys(layers).length) {
    entities.sector[sectorId].layers = Object.keys(layers).reduce(
      (result, layerId) => ({ ...result, [layerId]: true }),
      {},
    );
  }

  if (errors.length) {
    throw new SectorImportError(errors);
  }
  return { sectorId, entities, factions, layers, routes };
};

export const summarizeSectorImport = imported => {
  const entitiesByType = Object.keys(imported.entities).reduce(
    (summary, entityType) => ({
      ...summary,
      [entityType]: Object.keys(imported.entities[entityType] || {}).length,
    }),
    {},
  );
  const allEntities = Object.keys(imported.entities).reduce(
    (list, entityType) => [
      ...list,
      ...Object.keys(imported.entities[entityType] || {}).map(
        entityId => imported.entities[entityType][entityId],
      ),
    ],
    [],
  );
  const notes = Object.keys(imported.entities.note || {}).map(
    noteId => imported.entities.note[noteId],
  );
  const sector = imported.entities.sector[imported.sectorId];
  const entityIdentity = Object.keys(imported.entities).reduce(
    (identity, entityType) => ({
      ...identity,
      ...Object.keys(imported.entities[entityType] || {}).reduce(
        (mapping, entityId) => ({
          ...mapping,
          [entityId]: { entityId, entityType },
        }),
        {},
      ),
    }),
    {},
  );
  const childIndex = Object.keys(imported.entities).reduce(
    (index, entityType) =>
      Object.keys(imported.entities[entityType] || {}).reduce(
        (mapping, entityId) => {
          const entity = imported.entities[entityType][entityId];
          if (!entity.parent) {
            return mapping;
          }
          return {
            ...mapping,
            [entity.parent]: [
              ...(mapping[entity.parent] || []),
              entityIdentity[entityId],
            ],
          };
        },
        index,
      ),
    {},
  );
  const buildHierarchy = (entityId, entityType) => {
    const entity = imported.entities[entityType][entityId];
    return {
      entityId,
      entityType,
      name: entity.name,
      isHidden: !!entity.isHidden,
      children: (childIndex[entityId] || []).map(child =>
        buildHierarchy(child.entityId, child.entityType),
      ),
    };
  };
  const warnings = [];
  notes
    .filter(note => !note.isHidden)
    .forEach(note =>
      warnings.push(`Note "${note.name}" is visible to players.`),
    );
  Object.keys(imported.entities).forEach(entityType => {
    Object.keys(imported.entities[entityType] || {}).forEach(entityId => {
      const entity = imported.entities[entityType][entityId];
      const description = (entity.attributes || {}).description || '';
      if (/^\s*(hidden|not hidden)\s*$/im.test(description)) {
        warnings.push(
          `${entityType} "${entity.name}" contains visibility metadata in its description.`,
        );
      }
      ((entity.attributes || {}).tags || []).forEach(tag => {
        const knownTags = Entities[entityType].selectableTags || {};
        if (!knownTags[tag]) {
          warnings.push(
            `${entityType} "${entity.name}" uses custom or unknown tag "${tag}".`,
          );
        }
      });
      if (
        entityType === Entities.planet.key &&
        ['hundredsOfMillions', 'billions'].indexOf(
          (entity.attributes || {}).population,
        ) >= 0 &&
        entity.isHidden
      ) {
        warnings.push(
          `Major population center "${entity.name}" is hidden from players.`,
        );
      }
    });
  });
  return {
    name: sector.name,
    rows: sector.rows,
    columns: sector.columns,
    entities: allEntities.length,
    entitiesByType,
    hiddenEntities: allEntities.filter(entity => entity.isHidden).length,
    notes: notes.length,
    hiddenNotes: notes.filter(note => note.isHidden).length,
    factions: Object.keys(imported.factions).length,
    assets: Object.keys(imported.factions).reduce(
      (count, factionId) =>
        count + Object.keys(imported.factions[factionId].assets || {}).length,
      0,
    ),
    layers: Object.keys(imported.layers).length,
    routes: Object.keys(imported.routes).length,
    hierarchy: buildHierarchy(imported.sectorId, Entities.sector.key),
    warnings,
  };
};
